import React from 'react';
import type { ActivityLogEntry } from '../../types/ActivityLog';
import { formatActivityAction } from '../../types/ActivityLog';
import { format, formatDistanceToNow } from 'date-fns';
import { useChildrenNames } from '../../hooks/useChildrenNames';
import { useFamilyStore } from '../../stores/familyStore';

interface ActivityLogListProps {
  activities: ActivityLogEntry[];
  isLoading: boolean;
  error: string | null;
}

export const ActivityLogList: React.FC<ActivityLogListProps> = ({ activities, isLoading, error }) => {
  const { getChildName } = useChildrenNames();
  const { family } = useFamilyStore();
  
  // Format activity details (kept here, but translated to English)
  const formatActivityDetails = (entry: ActivityLogEntry): string => {
    // Extract date from entity_name
    const extractDate = (entityName?: string): string => {
      if (!entityName) return '';
      const match = entityName.match(/^(\d{4}-\d{2}-\d{2})/);
      return match ? match[1] : '';
    };
    
    const date = extractDate(entry.entity_name);

    // Try to build a from→to description based on known metadata keys
    const getChangeFromTo = (m: Record<string, any> | undefined): string => {
      if (!m) return '';
      const pairs: Array<[string, string]> = [
        ['old_location', 'new_location'],
        ['old_assignment', 'new_assignment'],
        ['previous_assignment', 'current_assignment'],
        ['from_state', 'to_state'],
        ['from', 'to'],
        ['old_value', 'new_value']
      ];
      const formatValue = (v: any): string => {
        if (v === 'home_a') return family?.home_a_name || 'Home A';
        if (v === 'home_b') return family?.home_b_name || 'Home B';
        if (v === null || v === undefined || v === '') return '—';
        if (typeof v === 'string') return v;
        try { return JSON.stringify(v); } catch { return String(v); }
      };
      for (const [fromKey, toKey] of pairs) {
        if (m[fromKey] !== undefined || m[toKey] !== undefined) {
          const fromVal = formatValue(m[fromKey]);
          const toVal = formatValue(m[toKey]);
          if (fromVal || toVal) return `${fromVal || '—'} → ${toVal || '—'}`;
        }
      }
      return '';
    };

    const change = getChangeFromTo(entry.metadata);

    // Parse title and date from entity_name or metadata.start_at (for events/notes/expenses)
    const parseTitleAndDate = (entityName?: string, meta?: Record<string, any>): { title?: string; date?: string } => {
      let parsedTitle: string | undefined;
      let parsedDate: string | undefined;
      if (entityName && entityName.includes(' @ ')) {
        const lastAt = entityName.lastIndexOf(' @ ');
        parsedTitle = entityName.slice(0, lastAt).trim() || undefined;
        parsedDate = entityName.slice(lastAt + 3).trim() || undefined;
      } else if (entityName) {
        const m = entityName.match(/^(\d{4}-\d{2}-\d{2})/);
        if (m) parsedDate = m[1];
      }
      if (!parsedDate && meta?.start_at) {
        try { parsedDate = new Date(meta.start_at).toISOString().slice(0,10); } catch {}
      }
      return { title: parsedTitle, date: parsedDate };
    };

    // Handle legacy entries without metadata (keep UI in English)
    if (!entry.metadata) {
      const { title, date } = parseTitleAndDate(entry.entity_name, undefined);
      switch (entry.action) {
        case 'note_created':
          return `Added note${title ? ` "${title}"` : ''}${date ? ` (${date})` : ''}`;
        case 'note_updated':
          return `Updated note${title ? ` "${title}"` : ''}${date ? ` (${date})` : ''}`;
        case 'note_deleted':
          return `Deleted note${title ? ` "${title}"` : ''}${date ? ` (${date})` : ''}`;
        case 'event_created':
          return `Added event${title ? ` "${title}"` : ''}${date ? ` (${date})` : ''}`;
        case 'event_updated':
          return `Updated event${title ? ` "${title}"` : ''}${date ? ` (${date})` : ''}`;
        case 'event_deleted':
          return `Deleted event${title ? ` "${title}"` : ''}${date ? ` (${date})` : ''}`;
        case 'expense_created':
          return `Added expense${title ? ` "${title}"` : ''}${date ? ` (${date})` : ''}`;
        case 'expense_updated':
          return `Updated expense${title ? ` "${title}"` : ''}${date ? ` (${date})` : ''}`;
        case 'expense_deleted':
          return `Deleted expense${title ? ` "${title}"` : ''}${date ? ` (${date})` : ''}`;
        default:
          // Fallback: show raw details (may still be PL for unknown actions)
          return entry.details || '';
      }
    }

    // We have metadata – use richer English description
    const childId = entry.metadata.child_id;
    const childName = getChildName(childId);
    
    switch (entry.action) {
      case 'calendar_entry_created': {
        return `Added calendar entry for ${childName}${date ? ` on ${date}` : ''}`;
      }
      case 'calendar_entry_updated': {
        const changeStr = change ? `: ${change}` : '';
        return `Updated calendar entry for ${childName}${date ? ` on ${date}` : ''}${changeStr}`;
      }
      case 'calendar_entry_deleted': {
        return `Deleted calendar entry for ${childName}${date ? ` from ${date}` : ''}`;
      }
      // Events / Notes / Expenses
      case 'event_created': {
        const { title: t, date: d } = parseTitleAndDate(entry.entity_name, entry.metadata);
        const title = t ? ` "${t}"` : '';
        const child = childName ? ` for ${childName}` : '';
        return `Added event${title}${child}${d ? ` (${d})` : ''}`;
      }
      case 'event_updated': {
        const { title: t, date: d } = parseTitleAndDate(entry.entity_name, entry.metadata);
        const title = t ? ` "${t}"` : '';
        const changeStr = change ? `: ${change}` : '';
        const child = childName ? ` for ${childName}` : '';
        return `Updated event${title}${child}${d ? ` (${d})` : ''}${changeStr}`;
      }
      case 'event_deleted': {
        const { title: t, date: d } = parseTitleAndDate(entry.entity_name, entry.metadata);
        const title = t ? ` "${t}"` : '';
        const child = childName ? ` for ${childName}` : '';
        return `Deleted event${title}${child}${d ? ` (${d})` : ''}`;
      }
      case 'note_created': {
        const { title: t, date: d } = parseTitleAndDate(entry.entity_name, entry.metadata);
        const title = t ? ` "${t}"` : '';
        const child = childName ? ` for ${childName}` : '';
        return `Added note${title}${child}${d ? ` (${d})` : ''}`;
      }
      case 'note_updated': {
        const { title: t, date: d } = parseTitleAndDate(entry.entity_name, entry.metadata);
        const title = t ? ` "${t}"` : '';
        const child = childName ? ` for ${childName}` : '';
        return `Updated note${title}${child}${d ? ` (${d})` : ''}`;
      }
      case 'note_deleted': {
        const { title: t, date: d } = parseTitleAndDate(entry.entity_name, entry.metadata);
        const title = t ? ` "${t}"` : '';
        const child = childName ? ` for ${childName}` : '';
        return `Deleted note${title}${child}${d ? ` (${d})` : ''}`;
      }
      case 'expense_created': {
        const { title: t, date: d } = parseTitleAndDate(entry.entity_name, entry.metadata);
        const amount = entry.metadata?.amount ? ` ${entry.metadata.amount} ${entry.metadata.currency || 'USD'}` : '';
        const title = t ? ` "${t}"` : '';
        const child = childName ? ` for ${childName}` : '';
        return `Added expense${title}${child}${amount}${d ? ` (${d})` : ''}`;
      }
      case 'expense_updated': {
        const { title: t, date: d } = parseTitleAndDate(entry.entity_name, entry.metadata);
        const parts: string[] = [];
        if (entry.metadata?.old_amount !== undefined || entry.metadata?.new_amount !== undefined) {
          parts.push(`amount: ${valueToLabel(entry.metadata?.old_amount)} → ${valueToLabel(entry.metadata?.new_amount)}`);
        }
        if (entry.metadata?.old_currency || entry.metadata?.new_currency) {
          parts.push(`currency: ${entry.metadata?.old_currency || '—'} → ${entry.metadata?.new_currency || '—'}`);
        }
        const suffix = parts.length ? ` (${parts.join(', ')})` : '';
        const title = t ? ` "${t}"` : '';
        const child = childName ? ` for ${childName}` : '';
        return `Updated expense${title}${child}${d ? ` (${d})` : ''}${suffix}`;
      }
      case 'expense_deleted': {
        const { title: t, date: d } = parseTitleAndDate(entry.entity_name, entry.metadata);
        const title = t ? ` "${t}"` : '';
        const child = childName ? ` for ${childName}` : '';
        return `Deleted expense${title}${child}${d ? ` (${d})` : ''}`;
      }
      case 'member_added':
        return `Added user ${entry.metadata.email || ''} as ${formatRole(entry.metadata.role)}`;
      case 'member_removed':
        return `Removed user ${entry.metadata.email || ''} (${formatRole(entry.metadata.role)})`;
      case 'member_role_changed':
        return `Changed role of user ${entry.metadata.email || ''} from ${formatRole(entry.metadata.old_role)} to ${formatRole(entry.metadata.new_role)}`;
      case 'child_added':
        return `Added child: ${entry.metadata.name || ''}`;
      case 'child_updated':
        return `Updated child data: ${entry.metadata.name || ''}`;
      case 'child_removed':
        return `Removed child: ${entry.metadata.name || ''}`;
      case 'family_settings_updated': {
        const changes: string[] = [];
        if (entry.metadata.name_changed) changes.push('family name');
        if (entry.metadata.home_a_changed) changes.push('Home A name');
        if (entry.metadata.home_b_changed) changes.push('Home B name');
        return `Settings changed: ${changes.join(', ')}`;
      }
      case 'invitation_sent':
        return `Invitation sent to ${entry.metadata.email || ''} (role: ${formatRole(entry.metadata.role)})`;
      case 'invitation_accepted':
        return `Invitation for ${entry.metadata.email || ''} was accepted`;
      case 'invitation_rejected':
        return `Invitation for ${entry.metadata.email || ''} was rejected`;
      case 'system_update':
        return `System update: ${entry.details}`;
      default:
        return entry.details || '';
    }
  };

  if (isLoading) {
    return (
      <div className="p-4 flex justify-center">
        <div className="animate-spin rounded-full h-6 w-6 border-2 border-blue-500 border-t-transparent"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
        <p className="text-red-700 text-sm">Error: {error}</p>
      </div>
    );
  }

  if (!activities || activities.length === 0) {
    return (
      <div className="p-4 bg-gray-50 border border-gray-200 rounded-lg text-center">
        <p className="text-gray-600 text-sm">No activity to display</p>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      {activities.map((activity) => (
        <div 
          key={activity.id} 
          className="p-3 border border-gray-200 rounded-lg bg-white hover:bg-gray-50 transition-colors"
        >
          <div className="flex items-start">
            <div className="mr-3 mt-1">
              {getActivityIcon(activity.action)}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-gray-900 mb-0.5">
                {formatActivityAction(activity.action)}
              </p>
              <p className="text-xs text-gray-600">
                {formatActivityDetails(activity)}
              </p>
              <div className="flex items-center mt-1">
                <span className="text-xs text-gray-500">
                  {formatTimeAgo(activity.created_at)}
                  <span className="mx-1">•</span>
                  {formatAbsoluteDate(activity.created_at)}
                </span>
                {(activity.user_email || activity.user_display_email) && (
                  <>
                    <span className="mx-1 text-gray-400">•</span>
                    <span className="text-xs text-gray-500">
                      {activity.user_display_email || activity.user_email}
                    </span>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};

function formatTimeAgo(dateString: string): string {
  try {
    const date = new Date(dateString);
    // Default locale is English; this will produce e.g. "about 10 hours ago"
    return formatDistanceToNow(date, { addSuffix: true });
  } catch (e) {
    return dateString;
  }
}

function formatAbsoluteDate(dateString: string): string {
  try {
    const date = new Date(dateString);
    return format(date, 'dd MMM yyyy, HH:mm');
  } catch (e) {
    return dateString;
  }
}

function getActivityIcon(action: string): React.ReactNode {
  const iconMap: Record<string, React.ReactNode> = {
    'calendar_entry_created': <span className="text-green-500">📅</span>,
    'calendar_entry_updated': <span className="text-blue-500">📅</span>,
    'calendar_entry_deleted': <span className="text-red-500">📅</span>,
    // Wydarzenia
    'event_created': <span className="text-green-500">📌</span>,
    'event_updated': <span className="text-blue-500">📌</span>,
    'event_deleted': <span className="text-red-500">📌</span>,
    // Notatki – kartka z piórem
    'note_created': <span className="text-green-500">📝</span>,
    'note_updated': <span className="text-blue-500">📝</span>,
    'note_deleted': <span className="text-red-500">📝</span>,
    // Koszty – pieniądze
    'expense_created': <span className="text-green-600">💸</span>,
    'expense_updated': <span className="text-blue-600">💸</span>,
    'expense_deleted': <span className="text-red-600">💸</span>,
    'member_added': <span className="text-green-500">👤</span>,
    'member_removed': <span className="text-red-500">👤</span>,
    'member_role_changed': <span className="text-blue-500">👤</span>,
    'child_added': <span className="text-green-500">👶</span>,
    'child_updated': <span className="text-blue-500">👶</span>,
    'child_removed': <span className="text-red-500">👶</span>,
    'family_settings_updated': <span className="text-blue-500">⚙️</span>,
    'invitation_sent': <span className="text-blue-500">📨</span>,
    'invitation_accepted': <span className="text-green-500">✉️</span>,
    'invitation_rejected': <span className="text-red-500">✉️</span>,
    'system_update': <span className="text-purple-500">🔄</span>,
  };

  return iconMap[action] || <span className="text-gray-500">📝</span>;
}

function formatRole(role?: string): string {
  if (!role) return '';
  const roleMap: Record<string, string> = {
    'parent_a': 'Parent (Admin)',
    'parent_b': 'Parent',
    'caregiver': 'Caregiver',
    'child_viewer': 'Viewer'
  };
  return roleMap[role] || role;
}

function valueToLabel(value: any): string {
  if (value === null || value === undefined) return '';
  if (typeof value === 'string') return value;
  try {
    return JSON.stringify(value);
  } catch {
    return String(value);
  }
}